// Group Buy table columns definition for reuse
import React from 'react';
import { Typography, Chip, Box, IconButton, Tooltip, Checkbox, LinearProgress, Avatar } from '@mui/material';
import { Visibility, Star, StarBorder, Edit } from '@mui/icons-material';

const getStatusColor = (status) => {
  switch (status) {
    case 'active': return 'success';
    case 'closed': return 'warning';
    case 'purchased': return 'info';
    case 'in_warehouse': return 'primary';
    case 'in_transit': return 'secondary';
    case 'in_nigeria': return 'info';
    case 'fulfilled': return 'success';
    case 'refund': return 'error';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount) => {
  return `₦${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const getGroupBuyColumns = ({
  selectedPools,
  setSelectedPools,
  handleViewPool,
  handleEditPool,
  handleFeatureToggle,
  updating
}) => [
  {
    field: 'sn',
    headerName: 'S/N',
    width: 70,
    align: 'center',
    headerAlign: 'center',
  },
  {
    field: 'select',
    headerName: '',
    width: 50,
    renderCell: (params) => (
      <Checkbox
        checked={selectedPools.includes(params.row.id)}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedPools(prev => [...prev, params.row.id]);
          } else {
            setSelectedPools(prev => prev.filter(id => id !== params.row.id));
          }
        }}
      />
    ),
  },
  {
    field: 'pool_image',
    headerName: 'Image',
    width: 80,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Avatar
          src={params.row.pic_url}
          alt={params.row.title}
          variant="rounded"
          sx={{
            width: 50,
            height: 50,
            border: '1px solid #e0e0e0',
            borderRadius: 1
          }}
        >
          <Typography variant="caption" color="textSecondary">
            No Img
          </Typography>
        </Avatar>
      </Box>
    ),
  },
  {
    field: 'title',
    headerName: 'Pool Title',
    width: 250,
    renderCell: (params) => (
      <Box>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={getStatusColor(params.value)}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    field: 'urgency_state',
    headerName: 'Urgency',
    width: 120,
    renderCell: (params) => {
      if (!params.value || params.value === 'CLOSED') return null;

      const getUrgencyColor = (state) => {
        switch (state) {
          case 'NEW': return 'success';
          case 'CLOSING_SOON': return 'warning';
          default: return 'default';
        }
      };

      return (
        <Chip
          label={params.value === 'NEW' ? '🆕 NEW' : params.value === 'CLOSING_SOON' ? '⏰ CLOSING SOON' : params.value}
          color={getUrgencyColor(params.value)}
          size="small"
          variant="filled"
          sx={{ fontWeight: 'bold' }}
        />
      );
    },
  },
  {
    field: 'progress',
    headerName: 'Progress',
    width: 150,
    renderCell: (params) => {
      const percentage = params.row.target_quantity > 0
        ? Math.min(100, Math.round((params.row.current_quantity / params.row.target_quantity) * 100))
        : 0;
      return (
        <Box sx={{ width: '100%' }}>
          <Typography variant="caption">
            {params.row.current_quantity}/{params.row.target_quantity}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
          />
        </Box>
      );
    },
  },
  {
    field: 'participant_count',
    headerName: 'Participants',
    width: 100,
    align: 'center',
    renderCell: (params) => (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2">{params.value}</Typography>
      </Box>
    ),
  },
  {
    field: 'pricing',
    headerName: 'Pricing',
    width: 150,
    renderCell: (params) => (
      <Box>
        <Typography variant="body2">
          {formatCurrency(params.row.discount_price || params.row.original_price)}
        </Typography>
        {params.row.discount_price && (
          <Typography variant="caption" color="success.main">
            Save {formatCurrency(params.row.original_price - params.row.discount_price)}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    field: 'initiator',
    headerName: 'Creator',
    width: 120,
    renderCell: (params) => (
      <Typography variant="body2">
        {params.value?.username || 'Unknown'}
      </Typography>
    ),
  },
  {
    field: 'created_at',
    headerName: 'Created',
    width: 150,
    renderCell: (params) => (
      <Typography variant="body2">
        {formatDate(params.value)}
      </Typography>
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 200,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" gap={1}>
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={() => handleViewPool(params.row)}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={params.row.status === 'active' ? "Cannot edit active pools" : "Edit Pool Details"}>
          <IconButton
            size="small"
            onClick={() => handleEditPool(params.row)}
            disabled={params.row.status === 'active'}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={params.row.is_featured ? "Unfeature" : "Feature"}>
          <IconButton
            size="small"
            onClick={() => handleFeatureToggle(params.row.id, params.row.is_featured)}
            disabled={updating}
          >
            {params.row.is_featured ? <Star /> : <StarBorder />}
          </IconButton>
        </Tooltip>
      </Box>
    )
  }
];